import { LitElement, html } from 'lit';
import { customElement, state, query, property } from 'lit/decorators.js';
import type { SlDialog } from '@shoelace-style/shoelace';

// Shoelace component imports
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/qr-code/qr-code.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';

import { styles } from './two-factor-auth-dialog-styles.js';
import { ApiFactory } from '../../api/agwasuite-api-factory.js';
import { ApiLogin } from '../../api/core/agwasuite-api-login.js';

// Define the possible states for the 2FA registration process
enum TwoFASetupState {
  PasswordVerification,
  QRCodeDisplay,
  Success,
}

@customElement('two-factor-auth-dialog')
export class TwoFactorAuthDialog extends LitElement {
  static styles = [styles];

  @property({ type: String })
  username = '';

  @state()
  private setupSecret = '';

  @state()
  private password = '';

  @state()
  private verificationCode = '';

  @state()
  private showErrorMessage = false;

  @state()
  private errorMessage = '';

  @state()
  private isLoading = false;

  @state()
  private setupState: TwoFASetupState = TwoFASetupState.PasswordVerification;

  @query('#twofa-dialog')
  private twofaDialog!: SlDialog;

  /**
   * Opens the two-factor authentication setup dialog
   */
  public async open() {
    this.setupState = TwoFASetupState.PasswordVerification;
    this.password = '';
    this.verificationCode = '';
    this.setupSecret = '';
    this.showErrorMessage = false;
    this.errorMessage = '';
    this.isLoading = false;
    void this.twofaDialog.show();
  }

  /**
   * Closes the dialog
   */
  public close() {
    void this.twofaDialog.hide();
  }

  private async verifyPassword() {
    if (!this.password) {
      this.showErrorMessage = true;
      this.errorMessage = 'Please enter your current password';
      return;
    }

    this.isLoading = true;
    this.showErrorMessage = false;

    try {
      const api = ApiFactory.authenticated(ApiLogin);
      const result = await api.registerTwoFactorAuthentication({
        username: this.username,
        password: this.password,
        staySignedIn: false,
      });

      if (result.success && result.data) {
        if (result.data.success) {
          // If the API call is successful but 2FA is already fully set up
          this.setupState = TwoFASetupState.Success;
        } else if (result.data.qrCodeUrl) {
          // If we got a QR code URL, move to the next step
          this.setupSecret = result.data.qrCodeUrl;
          this.setupState = TwoFASetupState.QRCodeDisplay;
        } else {
          // Some other issue occurred
          this.showErrorMessage = true;
          this.errorMessage =
            result.data.message || 'An error occurred during setup';
        }
      } else {
        this.showErrorMessage = true;
        this.errorMessage = 'Invalid password. Please try again.';
      }
    } catch (error) {
      this.showErrorMessage = true;
      this.errorMessage =
        'Failed to connect to the server. Please try again later.';
      console.error('2FA setup error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async verifyTwoFactor() {
    if (!this.verificationCode) {
      this.showErrorMessage = true;
      this.errorMessage =
        'Please enter the verification code from your authenticator app';
      return;
    }

    this.isLoading = true;
    this.showErrorMessage = false;

    try {
      const api = ApiFactory.authenticated(ApiLogin);
      const result = await api.registerTwoFactorAuthentication({
        username: this.username,
        password: this.password,
        twoFactorCode: this.verificationCode,
        staySignedIn: false,
      });

      if (result.success && result.data && result.data.success) {
        // Verification successful
        this.setupState = TwoFASetupState.Success;
        // Dispatch event to notify parent component about the successful verification
        this.dispatchEvent(new CustomEvent('verification-success'));
      } else {
        // Verification failed
        this.showErrorMessage = true;
        this.errorMessage =
          (result.data && result.data.message) ||
          'Invalid verification code. Please try again.';
      }
    } catch (error) {
      this.showErrorMessage = true;
      this.errorMessage =
        'Failed to connect to the server. Please try again later.';
      console.error('2FA verification error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private handlePasswordInput(event: Event) {
    this.password = (event.target as HTMLInputElement).value;
    this.showErrorMessage = false;
  }

  private handleVerificationInput(event: Event) {
    this.verificationCode = (event.target as HTMLInputElement).value;
    this.showErrorMessage = false;
  }

  private getPasswordVerificationTemplate() {
    return html`
      <div class="twofa-setup-container">
        <p>
          To set up two-factor authentication, please enter your current
          password to verify your identity.
        </p>
        <div class="form-field">
          <label>Username</label>
          <sl-input type="text" value="${this.username}" disabled></sl-input>
        </div>
        <div class="form-field">
          <label>Current Password</label>
          <sl-input
            id="password-input"
            type="password"
            placeholder="Enter your current password"
            @sl-change=${this.handlePasswordInput}
            ?disabled=${this.isLoading}
          ></sl-input>
        </div>

        ${
          this.showErrorMessage
            ? html`<sl-alert variant="danger" open>
                ${this.errorMessage}
              </sl-alert>`
            : null
        }
      </div>

      <div slot="footer">
        <sl-button
          variant="neutral"
          @click=${this.close}
          ?disabled=${this.isLoading}
        >
          Cancel
        </sl-button>
        <sl-button
          variant="primary"
          @click=${this.verifyPassword}
          ?disabled=${this.isLoading}
        >
          ${this.isLoading ? html`<sl-spinner></sl-spinner>` : 'Continue'}
        </sl-button>
      </div>
    `;
  }

  private getQRCodeTemplate() {
    return html`
      <div class="twofa-setup-container">
        <p>
          Scan the QR code below with your authenticator app (like Google
          Authenticator, Authy, or Microsoft Authenticator) to set up two-factor
          authentication.
        </p>
        <sl-qr-code value="${this.setupSecret}" class="qrcode"></sl-qr-code>

        <div class="verification-section">
          <p>
            Enter the 6-digit verification code from your authenticator app:
          </p>
          <sl-input
            id="verification-input"
            type="text"
            placeholder="Enter verification code"
            maxlength="6"
            pattern="[0-9]{6}"
            required
            @sl-change=${this.handleVerificationInput}
            ?disabled=${this.isLoading}
          >
          </sl-input>

          ${
            this.showErrorMessage
              ? html`<sl-alert variant="danger" open>
                  ${this.errorMessage}
                </sl-alert>`
              : null
          }
        </div>
      </div>

      <div slot="footer">
        <sl-button
          variant="neutral"
          @click=${() => {
            this.setupState = TwoFASetupState.PasswordVerification;
          }}
          ?disabled=${this.isLoading}
        >
          Back
        </sl-button>
        <sl-button
          variant="primary"
          @click=${this.verifyTwoFactor}
          ?disabled=${this.isLoading}
        >
          ${
            this.isLoading
              ? html`<sl-spinner></sl-spinner>`
              : 'Verify and Enable'
          }
        </sl-button>
      </div>
    `;
  }

  private getSuccessTemplate() {
    return html`
      <div class="twofa-setup-container success">
        <sl-icon name="check-circle-fill" class="success-icon"></sl-icon>
        <h3>Two-Factor Authentication Enabled</h3>
        <p>
          Two-factor authentication has been successfully enabled for your
          account. You will now be required to enter a verification code when
          signing in.
        </p>
        <p>
          <strong>Important:</strong> Keep your authenticator app accessible.
          Without it, you may lose access to your account.
        </p>
      </div>

      <div slot="footer">
        <sl-button variant="primary" @click=${this.close}> Close </sl-button>
      </div>
    `;
  }

  render() {
    let dialogTitle;
    let contentTemplate;

    switch (this.setupState) {
      case TwoFASetupState.PasswordVerification:
        dialogTitle = 'Verify Your Identity';
        contentTemplate = this.getPasswordVerificationTemplate();
        break;
      case TwoFASetupState.QRCodeDisplay:
        dialogTitle = 'Set Up Two-Factor Authentication';
        contentTemplate = this.getQRCodeTemplate();
        break;
      case TwoFASetupState.Success:
        dialogTitle = 'Setup Complete';
        contentTemplate = this.getSuccessTemplate();
        break;
    }

    return html`
      <sl-dialog
        id="twofa-dialog"
        label="${dialogTitle}"
        @sl-request-close=${(e: CustomEvent) => {
          if (e.detail.source === 'overlay') e.preventDefault();
        }}
      >
        ${contentTemplate}
      </sl-dialog>
    `;
  }
}
